// Testovací instance (dev 797093, SK 818282 — `instances.dev` manifestu shopu):
// pruh „Testovací verze" a blokace odeslání objednávky pro lidi.
//
// PROČ (incident 2026-09-21, `ops/sweeps/2026-09-21-objednavky-na-dev/`): dev je
// vizuálně i cenově zrcadlo ostré. Když na něj 11.–13. 9. vedly odkazy
// z našeptávače ostré, zákazníci nic nepoznali a vytvořili 5 objednávek, které
// do ostré nikdy nedorazily. Od 21. 9. je frontend dev za heslem Shoptetu, ale
// heslo jde v adminu vypnout — tohle je pojistka, která platí i pak.
//
// Pravidla (rozhodnutí Tomáše 2026-09-21, mock nad fixtures):
// - Rozhoduje `projectId` z dataLayeru, ne hostname (dev má i jiné domény,
//   Bender běží na localhost).
// - Automatizovaný prohlížeč (`navigator.webdriver`, session `qa-…`) pruh
//   nevidí a objednávku odeslat smí: paritní harnessy a smoke proti dev měří
//   beze změny, `tools/qa-agent/complete-order.mjs` funguje dál. Devcheck si
//   lidské chování vynutí `?bs-dev-guard=force` (drží se v sessionStorage).
// - Blokuje se jen finální odeslání (form#order-form → Step2Validate). Košík,
//   doprava, registrace ani formuláře ne.

import { DEV_NOTICE } from '../locale/cs/common.js';
import { projectId } from './features.js';
import SHOP from '../../config/shop.js';

// projekt testovací instance → origin ostré, kam posíláme lidi (z manifestu shopu;
// shop bez testovací instance má mapu prázdnou)
export const DEV_PROJECTS = SHOP.instances.dev ? { [SHOP.instances.dev]: SHOP.shop.origin } : {};

const FORCE_KEY = 'bs-dev-guard';
const QA_SID_KEY = 'bs-search-sid'; // tools/lib/qa-session.mjs SEARCH_SESSION_KEY
const QA_PREFIX = 'qa-';

function storage() {
  try {
    return window.sessionStorage;
  } catch (e) {
    return null; // blokované úložiště (Chrome „Blokovat všechny cookies")
  }
}

/** `?bs-dev-guard=force` → chovej se jako k člověku i v automatizaci (devcheck). */
function forced() {
  const s = storage();
  try {
    if (/[?&]bs-dev-guard=force\b/.test(window.location.search) && s) s.setItem(FORCE_KEY, 'force');
  } catch (e) { /* bez úložiště platí jen parametr */ }
  try {
    if (/[?&]bs-dev-guard=force\b/.test(window.location.search)) return true;
    return !!s && s.getItem(FORCE_KEY) === 'force';
  } catch (e) {
    return false;
  }
}

/** QA provoz: automatizovaný prohlížeč nebo session označená harnessem. */
export function isQaTraffic() {
  try {
    if (navigator.webdriver === true) return true;
  } catch (e) { /* nic */ }
  const s = storage();
  try {
    return !!s && String(s.getItem(QA_SID_KEY) || '').indexOf(QA_PREFIX) === 0;
  } catch (e) {
    return false;
  }
}

/** Origin ostré pro tuhle testovací instanci, jinak `null` (ostrá, jiný shop). */
export function liveOriginForDev(id) {
  return Object.prototype.hasOwnProperty.call(DEV_PROJECTS, id) ? DEV_PROJECTS[id] : null;
}

/** Stejná stránka na ostré (dev je zrcadlo, cesty sedí); pokladna → košík ostré. */
function liveHref(origin) {
  try {
    const path = window.location.pathname || '/';
    if (/^\/(objednavka|kosik)\//.test(path)) return origin + '/';
    return origin + path + (window.location.search || '').replace(/[?&]bs-dev-guard=force\b/, '');
  } catch (e) {
    return origin + '/';
  }
}

function el(tag, cls, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text) n.textContent = text;
  return n;
}

function mountBar(origin) {
  if (document.querySelector('.bs-devbar')) return;
  const bar = el('div', 'bs-devbar');
  bar.setAttribute('role', 'note');
  const msg = el('span', 'bs-devbar__msg');
  msg.appendChild(el('strong', null, DEV_NOTICE.barTitle));
  msg.appendChild(document.createTextNode(DEV_NOTICE.barText));
  const link = el('a', 'bs-devbar__cta', DEV_NOTICE.barCta);
  link.href = liveHref(origin);
  bar.appendChild(msg);
  bar.appendChild(link);
  document.body.insertBefore(bar, document.body.firstChild);
}

function orderForm() {
  const form = document.getElementById('order-form');
  return form && /Step2Validate/i.test(form.getAttribute('action') || '') ? form : null;
}

function blockNotice(origin) {
  const box = el('div', 'bs-devblock');
  box.setAttribute('role', 'alert');
  box.setAttribute('tabindex', '-1');
  box.appendChild(el('strong', 'bs-devblock__title', DEV_NOTICE.blockTitle));
  box.appendChild(el('p', 'bs-devblock__text', DEV_NOTICE.blockText));
  const link = el('a', 'bs-devblock__cta', DEV_NOTICE.blockCta);
  link.href = origin + '/';
  box.appendChild(link);
  return box;
}

function mountOrderBlock(origin, form) {
  const box = blockNotice(origin);
  // nad nativní blok CTA (`.next-step`) jako sourozenec — mobil `.next-step`
  // schovává (CTA nese lišta `.bs-co-bar`), hláška musí zůstat vidět
  const anchor = document.querySelector('.next-step') || form;
  anchor.parentNode.insertBefore(box, anchor);

  const reveal = function () {
    try {
      box.scrollIntoView({ block: 'center', behavior: 'smooth' });
      box.focus({ preventScroll: true });
    } catch (e) { /* starý prohlížeč */ }
  };
  const stop = function (event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    reveal();
  };
  // Stejná mechanika jako pickup guard pokladny (checkoutPage.js): CTA stojí mimo
  // form (`form="order-form"`), lišta na mobilu jen pouští `live.click()` a Enter
  // je syntetický klik — capture na window je dřív než všechny handlery stránky.
  window.addEventListener(
    'click',
    function (event) {
      const btn = event.target && event.target.closest ? event.target.closest('button, input[type="submit"]') : null;
      if (!btn || btn.form !== form || (btn.type && btn.type !== 'submit')) return;
      stop(event);
    },
    true,
  );
  form.addEventListener('submit', stop, true);
  // programové `form.submit()` event `submit` nevyvolá — přebít i tu cestu
  form.submit = reveal;
}

export function initDevInstance() {
  const origin = liveOriginForDev(projectId());
  if (!origin) return;
  if (isQaTraffic() && !forced()) return;
  document.documentElement.classList.add('bs-dev-instance');
  mountBar(origin);
  const form = orderForm();
  if (form) mountOrderBlock(origin, form);
}
